import { InsightDescriptor, PoiId } from 'utils/entities';
import { State } from 'store/state';
import { createSelector } from 'reselect';
import { head, flow, groupBy, sortBy, last, capitalize } from 'lodash';

import { PricesMapFeatureCollection, PricesMapMode, PricesMapOwnProps } from './types';
import config from 'config';
import { lessThanM, priceGrid } from 'locale/formatMetric';
import {
  RawHistoryItem,
  IPriceEstimationRecord,
  referenceMapper,
  pricesStateSelector,
  PricesTabType,
  IBulletinPriceEstimationRecord,
  IHistoricalPriceEstimationRecord,
} from 'store/state/selectors/insights/prices';
import { makeInsightsByTypeSelector } from 'store/state/domainData/selectors';
import { pricesTabSelector } from 'store/state/insightsContext/selectors';
import { visitedPoiIdsSelector, savedPoiIdsSelector, activePoiIdsSelector } from 'store/state/searchContext/selectors';
import { IBulletinFeatureProps } from 'components/map/types';
import { UnitRelevancy, getRelevancy } from 'components/unit-page/RelevancyLabel';
import { PricesHistorySummary } from 'store/state/selectors/insights/summaryTypes';

export const activeEstimationIdxSelector = flow(pricesStateSelector, (p) => p.activeEstimationIndex);
export const activeHistoryIdxSelector = flow(pricesStateSelector, (p) => p.activeHistoryIndex);

const moneyFormatterSelector = (_: State, props: PricesMapOwnProps) => props.formatMoneyWithMetricPrefix;

export const pricesMapModeSelector = createSelector([
  pricesTabSelector,
  (_: State, props: PricesMapOwnProps) => props.mode,
], (tab, mode): PricesMapMode => {
  if (mode) return mode;

  switch (tab) {
    case PricesTabType.CompareDeals:
      return 'estimation';
    case PricesTabType.Area:
      return 'history';
    case PricesTabType.Compare:
      return 'listings';
    case PricesTabType.Building:
      return 'history';
    default:
      return null;
  }
});

const localHistoryListSelector = createSelector([
  flow(makeInsightsByTypeSelector(InsightDescriptor.Prices), head),
  pricesTabSelector,
  pricesMapModeSelector,
], (insight, activeTab, mode): RawHistoryItem[] => {

  if (!insight) return [];
  const summary: PricesHistorySummary = insight.summary.nonText.data as any;
  return mode === 'history' ? summary[activeTab] || [] : [];
});


export const makeLocalHistoryGeoSelector = () => createSelector([
  localHistoryListSelector,
  activeHistoryIdxSelector,
  moneyFormatterSelector,
  () => config.locale,
], (list, hoveredId, formatter, locale) => {

  const grouped = groupBy(list.filter(i => i.addressRecord && i.addressRecord.location), (i) => `${i.addressRecord.location.lat.toFixed(5)}_${i.addressRecord.location.lng.toFixed(5)}`);

  const geo: PricesMapFeatureCollection = {
    type: 'FeatureCollection',
    features: Object.values(grouped).map((historyItems): GeoJSON.Feature<GeoJSON.Point, any> => {
      const idxList = historyItems.map(i => i.saleId);
      const highestPrice = last(sortBy(historyItems, 'amount')).amount;
      const {
        amount,
        date,
        beds,
        size,
        projectName: projectName,
        projectIds: projectIds,
        yearBuilt: constructionYear,
        pricePerMeter: pricePerSquareMeter,
        floor,
        addressRecord: {
          location,
        },
        saleId: soldId,
        addressRecord: addressDetails,
      } = last(sortBy(historyItems, (i) => +new Date(i.date)));
      const price = locale === 'he-IL' && lessThanM(amount)
          ? formatter(amount, 2, priceGrid.m)
          : formatter(amount);
      return location ? {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [ location.lng, location.lat ],
        },
        properties: {
          isActive: idxList.includes(hoveredId[0]),
          id: idxList,
          type: 'historical',
          beds,
          locationPoint: [ location.lng, location.lat ],
          size,
          groupLength: historyItems.length,
          price,
          floor,
          soldId,
          constructionYear,
          projectName,
          projectIds,
          pricePerSquareMeter,
          addressDetails: {
            unitNumber: addressDetails.hNo,
            borough: addressDetails.borough,
            city: addressDetails.city,
            docId: addressDetails.docId,
            neighbourhood: addressDetails.neighbourhood,
            streetName: addressDetails.stName && addressDetails.stName.split(' ').map(capitalize).join(' '),
            streetNumber: addressDetails.hNo,
          },
          saleDate: date,
          brokerDeal: false,
          highestPrice,
        },
      } : null;
    }).filter(Boolean),
  };

  return geo.features.length ? geo : null;
});

const estimationInsightSelector = flow(makeInsightsByTypeSelector(InsightDescriptor.PricesEstimation), head);

const estimationListSelector = createSelector([
  estimationInsightSelector,
], (insight): IPriceEstimationRecord[] => {
  return (insight && insight.summary
    ? (insight.summary.nonText.data as any).refListings
      .map(referenceMapper)
      .filter((i: IPriceEstimationRecord) => i.type === 'deal')
    : []);
});

export const makeEstimationGeoSelector = () => createSelector([
  estimationListSelector,
  activeEstimationIdxSelector,
  moneyFormatterSelector,
], (list, estimationIdx, formatter) => {
  const grouped = groupBy(list, (i) => `${i.location[0].toFixed(5)}_${i.location[1].toFixed(5)}`);
  const itemToIdxMap = new WeakMap(list.map((item, index) => [ item, index + 1 ]));

  const geo: PricesMapFeatureCollection = {
    type: 'FeatureCollection',
    features: Object.values(grouped).map((estimationItems: IHistoricalPriceEstimationRecord[]): GeoJSON.Feature<GeoJSON.Point, any> => {
      const idxList = estimationItems.map(i => itemToIdxMap.get(i));
      const highestPrice = last(sortBy(estimationItems, 'price')).price;
      const { price, date, location, address, pricePerSquareMeter, area, rooms, constructionYear, soldId } = last(sortBy(estimationItems, (i) => +new Date(i.date)));

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: location,
        },
        properties: {
          isActive: idxList.includes(estimationIdx[0]),
          id: idxList,
          soldId,
          type: 'historical',
          groupLength: estimationItems.length,
          price: formatter(price),
          address,
          saleDate: date,
          brokerDeal: false,
          highestPrice,
          locationPoint: location,
          pricePerSquareMeter,
          constructionYear,
          size: area,
          beds: rooms,
        },
      };
    }),
  };


  return geo.features.length ? geo : null;
});

export const COMPARE_TAB_LISTINGS_LIMIT = 9;

export const comparableListingsListSelector = createSelector([
  estimationInsightSelector,
], (insight): IBulletinPriceEstimationRecord[] => {
  const bulletins = (insight && insight.summary
    ? (insight.summary.nonText.data as any).refListings
      .map(referenceMapper)
      .filter((i: IPriceEstimationRecord) => i.type === 'bulletin' && i.location)
    : []);
  return bulletins.length ? bulletins.slice(1, COMPARE_TAB_LISTINGS_LIMIT) : [];
});

const groupedListingsSelector = createSelector([
  comparableListingsListSelector,
], (comparableListings): Record<string, IBulletinPriceEstimationRecord[]> => {
  return groupBy(comparableListings, (listing) => listing.docId);
});


const makeBulletinProperties = (group: IBulletinPriceEstimationRecord[], isActive: boolean, moneyFormatter: (value: number, digits?: number, enforcedVal?: number) => string, visited: Set<PoiId>, saved: Set<PoiId>): IBulletinFeatureProps => {
  const isMulti = group.length > 1;
  const [ poi ] = group;
  return {
    featureType: 'targetPoint',
    isNew: group.some((b) => getRelevancy({ createdDate: b.type === 'bulletin' && b.firstTimeSeen, isInLastViewed: false, isBulletinType: true }) === UnitRelevancy.New),
    isAgent: poi.type === 'bulletin' && poi.poc && poi.poc.type === 'agent',
    type: 'bulletin',
    ids: group.map(record => record.id),
    groupLength: group.length,
    price: isMulti
      ? null
      : (lessThanM(poi.price) && config.locale === 'he-IL') ? moneyFormatter(poi.price, 6, priceGrid.m) : moneyFormatter(poi.price),
    isActive,
    isVisited: group.every(({ id }) => visited.has(id)),
    isSaved: isMulti ? null : saved.has(poi.id),
  };
};

export const makeComparableListingsGeoSelector = () => createSelector([
  groupedListingsSelector,
  activePoiIdsSelector,
  visitedPoiIdsSelector,
  savedPoiIdsSelector,
  moneyFormatterSelector,
], (grouped, activePoiIds, visitedPoiIds, savedPoiIds, formatter): GeoJSON.FeatureCollection<GeoJSON.Point, IBulletinFeatureProps> => {

  const features: Array<GeoJSON.Feature<GeoJSON.Point, IBulletinFeatureProps>> = Object.values(grouped).map(group => {
    const [ poi ] = group;
    const isActive = group.some(item => activePoiIds.has(item.id));

    const properties = makeBulletinProperties(group, isActive, formatter, visitedPoiIds, savedPoiIds);
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: poi.location,
      },
      properties,
    };
  });

  if (!features.length) return null;

  return {
    type: 'FeatureCollection',
    features,
  };
});

export const activeComparableListingCoordsSelector = createSelector([
  comparableListingsListSelector,
  activePoiIdsSelector,
], (list, activePoIds) => {
  const activeListing = list.find(item => activePoIds.has(item.id));
  return activeListing && activeListing.location;
});

export const makeHoveredFeatureGeoSelector = () => {
  const estimationGeoSelector = makeEstimationGeoSelector();
  const localHistoryGeoSelector = makeLocalHistoryGeoSelector();

  return createSelector([
    pricesMapModeSelector,
    estimationGeoSelector,
    localHistoryGeoSelector,
    activeEstimationIdxSelector,
    activeHistoryIdxSelector,
  ], (mode, estimationGeo, localHistoryGeo, activeEstimationIdx, activeHistoryIdx) => {
    let hoveredFeature = null;

    if (mode === 'estimation' && estimationGeo && activeEstimationIdx.length) {
      const [ activeIdx ] = activeEstimationIdx;
      hoveredFeature = estimationGeo.features
        .find(({ properties: { id } }) => id.includes(activeIdx));
    }
    else if (mode === 'history' && localHistoryGeo && activeHistoryIdx.length) {
      const [ activeIdx ] = activeHistoryIdx;
      hoveredFeature = localHistoryGeo.features
        .find(({ properties: { id } }) => id.includes(activeIdx));
    }

    const hoveredFeatureGeo: GeoJSON.FeatureCollection<GeoJSON.Point, any> = {
      type: 'FeatureCollection',
      features: [ hoveredFeature ],
    };

    return hoveredFeature ? hoveredFeatureGeo : null;
  });

};
